import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, RotateCcw, Check } from 'lucide-react';

interface CameraCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
}

/**
 * Live-camera capture modal using getUserMedia.
 * Caller should first attempt getUserMedia synchronously inside the user
 * click handler (browser gesture requirement) before opening this modal —
 * if that fails, fall back to a hidden <input type="file" capture> instead.
 */
export function CameraCaptureModal({ open, onClose, onCapture }: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facingMode } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Camera unavailable');
      }
    })();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [open, facingMode]);

  const handleShutter = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setSnapshot(canvas.toDataURL('image/jpeg', 0.92));
  };

  const handleUse = () => {
    if (!snapshot) return;
    const [, base64] = snapshot.split(',');
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const file = new File([bytes], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
    onCapture(file);
    setSnapshot(null);
    onClose();
  };

  const handleClose = () => {
    setSnapshot(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex flex-col bg-black"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3 text-white">
            <button
              onClick={handleClose}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 backdrop-blur"
              aria-label="Close camera"
            >
              <X className="h-5 w-5" />
            </button>
            <p className="font-body text-xs uppercase tracking-widest opacity-80">Take Photo</p>
            <button
              onClick={() => setFacingMode((m) => (m === 'user' ? 'environment' : 'user'))}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 backdrop-blur"
              aria-label="Switch camera"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>

          {/* Viewport */}
          <div className="relative flex-1 overflow-hidden bg-black">
            {error ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-white">
                <p className="font-body text-sm opacity-90">Camera unavailable</p>
                <p className="font-body text-xs opacity-60 max-w-xs">{error}</p>
              </div>
            ) : snapshot ? (
              <img src={snapshot} alt="Preview" className="h-full w-full object-contain" />
            ) : (
              <video
                ref={videoRef}
                playsInline
                muted
                className="h-full w-full object-cover"
              />
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-8 px-6 pb-10 pt-6 bg-black">
            {snapshot ? (
              <>
                <button
                  onClick={() => setSnapshot(null)}
                  className="rounded-full bg-white/10 px-5 py-2.5 font-body text-sm text-white backdrop-blur"
                >
                  Retake
                </button>
                <button
                  onClick={handleUse}
                  className="flex items-center gap-2 rounded-full bg-white px-6 py-2.5 font-body text-sm font-medium text-black"
                >
                  <Check className="h-4 w-4" /> Use Photo
                </button>
              </>
            ) : (
              <button
                onClick={handleShutter}
                disabled={!!error}
                className="flex h-16 w-16 items-center justify-center rounded-full bg-white ring-4 ring-white/30 disabled:opacity-40"
                aria-label="Capture"
              >
                <Camera className="h-6 w-6 text-black" />
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Try to open the live camera. Returns true if getUserMedia succeeded
 * (caller should show CameraCaptureModal). Returns false if unavailable
 * so the caller can fall back to a hidden file input.
 *
 * Must be invoked synchronously from within a user-gesture handler.
 */
export async function probeCameraAvailable(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return false;
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    stream.getTracks().forEach((t) => t.stop());
    return true;
  } catch {
    return false;
  }
}
